import React from "react";
import { ImageIcon } from "lucide-react";

/**
 * iter72g · ProductThumbnail — shared thumbnail for catalog cards + rows.
 *
 * Renders the first available image on a catalog product. Falls back to
 * a subtle placeholder icon when no image is set (per user spec) so the
 * layout stays tidy across the vendor's own edit view and the club-side
 * B2B browse view.
 *
 * Usage:
 *   <ProductThumbnail product={p} size="card" />   // 100% wide, 4:3
 *   <ProductThumbnail product={p} size="row" />    // 64x64 square
 */
export default function ProductThumbnail({ product, size = "card", testId }) {
  const imageId =
    product?.primary_image_id ||
    (product?.image_ids && product.image_ids[0]) ||
    null;

  const extraCount = Math.max(0, (product?.image_ids?.length || 0) - 1);
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const src = imageId ? `${backend}/api/files/${imageId}` : null;

  const sizeClass = size === "row"
    ? "w-16 h-16 shrink-0"
    : "w-full aspect-[4/3]";

  return (
    <div
      className={`relative ${sizeClass} bg-[#F3F1E9] border border-[#E5E3D8] overflow-hidden`}
      data-testid={testId || `product-thumb-${product?.product_id || "unknown"}`}
    >
      {src ? (
        <img
          src={src}
          alt={product?.product_name || "Product"}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#B59A5A]/50">
          <ImageIcon size={size === "row" ? 22 : 40} strokeWidth={1.25} />
        </div>
      )}
      {extraCount > 0 && (
        <span
          className="absolute bottom-1.5 right-1.5 text-[10px] uppercase tracking-[0.14em] bg-white/95 text-[#1A422D] px-1.5 py-0.5 border border-[#E5E3D8]"
          data-testid={`product-thumb-extra-${product?.product_id || "unknown"}`}
        >
          +{extraCount}
        </span>
      )}
    </div>
  );
}
